//_components/onboarding/registration/AccountRegisterStep2.tsx
import { motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";

type Step2Props = {
  onBack: () => void;
  onNext: (role: string) => void;
};

export default function AccountRegisterStep2({ onBack, onNext }: Step2Props) {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  const roles = [
    {
      id: "manufacturer",
      label: "Manufacturer",
      icon: "/assets/icons/solar-buildings.svg",
    },
    {
      id: "distributor",
      label: "Distributor",
      icon: "/assets/icons/product-loading.svg",
    },
    { id: "recycler", label: "Recycler", icon: "/assets/icons/recycle.svg" },
    { id: "homeowner", label: "Homeowner", icon: "/assets/icons/home.svg" },
  ];

  const handleSelect = (id: string) => {
    setSelectedRole(id);
  };

  const handleContinue = () => {
    if (selectedRole) {
      onNext(selectedRole);
    }
  };

  return (
    <div className="text-center">
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-semibold text-gray-800"
      >
        How do you want to use Ren Power Group?
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-2 text-sm text-gray-600"
      >
        Choose if you're here to find solar panels, batteries, or recycling
        services.
      </motion.p>

      <div className="mt-8 space-y-4">
        {roles.map((role) => (
          <motion.button
            key={role.id}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleSelect(role.id)}
            className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
              selectedRole === role.id
                ? "border-green-700 bg-white"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="flex items-center space-x-4">
              <Image src={role.icon} width={24} height={24} alt={role.label} />
              <span className="text-lg font-medium text-gray-800">
                {role.label}
              </span>
            </div>
            <div
              className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                selectedRole === role.id
                  ? "border-green-600"
                  : "border-gray-300"
              }`}
            >
              {selectedRole === role.id && (
                <div className="h-3 w-3 rounded-full bg-green-600"></div>
              )}
            </div>
          </motion.button>
        ))}
      </div>

      <div className="mt-10 flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-100"
        >
          ← Back
        </button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          disabled={!selectedRole}
          onClick={handleContinue}
          className={`px-6 py-3 rounded-full font-medium ${
            selectedRole
              ? "bg-green-800 text-white hover:bg-green-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Continue
        </motion.button>
      </div>
    </div>
  );
}
